import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { RefreshCw, X } from "lucide-react";
import Slider from "@mui/material/Slider";
import PropTypes from "prop-types";

export default function BlockEditorModal({ block, anchorRect, boundsRect, onClose, onSave, onLiveChange }) {
    const [draft, setDraft] = useState("");
    const [fontSize, setFontSize] = useState(16);
    const originalRef = useRef("");
    const areaRef = useRef(null);
    

    useEffect(() => {
        if (!block) return;
        setDraft(block.content);
        originalRef.current = block.content;
        setFontSize(16);
        const id = window.setTimeout(() => areaRef.current?.focus(), 120);
        return () => window.clearTimeout(id);
    }, [block?.id]);

    useEffect(() => {
        if (!block) return;
        const onKey = (e) => {
            if (e.key === "Escape") cancel();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    });

    function cancel() {
        if (block) onLiveChange(block.id, originalRef.current);
        onClose();
    }

    const rect = anchorRect || {
        top: 0,
        left: 0,
        width: 0,
        height: 0,
    };


    const bounds = boundsRect || {
        top: 0,
        left: 0,
        width: rect.width,
        height: rect.height,
    };

    const originOffset = {
        x: rect.left + rect.width / 2 - (bounds.left + bounds.width / 2),
        y: rect.top + rect.height / 2 - (bounds.top + bounds.height / 2),
    };

    return (
        <AnimatePresence>
            {block && (
                <div
                    style={{
                        position: "fixed",
                        top: bounds.top,
                        left: bounds.left,
                        width: bounds.width,
                        height: bounds.height,
                        zIndex: 1300,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 16,
                        boxSizing: "border-box",
                        overflow: "hidden",
                    }}
                >
                    <motion.div
                        style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(15,15,20,0.35)",
                            backdropFilter: "blur(2px)",
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={cancel}
                    />

                    <motion.div
                        style={{
                            position: "relative",
                            zIndex: 1,
                            width: "100%",
                            maxWidth: 560,
                            overflow: "hidden",
                            borderRadius: 16,
                            border: "1px solid #e4e4e7",
                            background: "#ffffff",
                            boxShadow: "0 20px 60px rgba(0,0,0,.18)",
                        }}
                        initial={{
                            opacity: 0,
                            scale: 0.35,
                            x: originOffset.x,
                            y: originOffset.y,
                        }}
                        animate={{ opacity: 1, scale: 1, x: 0, y: 0 }}
                        exit={{
                            opacity: 0,
                            scale: 0.35,
                            x: originOffset.x,
                            y: originOffset.y,
                        }}
                        transition={{ type: "spring", stiffness: 320, damping: 30 }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                borderBottom: "1px solid #f1f5f9",
                                padding: "12px 20px",
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: "#27272a",
                                    textTransform: "capitalize",
                                }}
                            >
                                {block.label}
                            </span>

                            <button
                                onClick={cancel}
                                style={{
                                    border: "none",
                                    background: "transparent",
                                    cursor: "pointer",
                                    color: "#94a3b8",
                                    display: "flex",
                                    padding: 4,
                                }}
                            >
                                <X size={16} />
                            </button>
                        </div>

                        <div style={{ padding: "16px 20px 0" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent:"flex-end" }}>
                                <span style={{ fontSize: 12, color: "#71717a", fontWeight: 500, whiteSpace: "nowrap" }}>
                                    Font size:
                                </span>

                                <span style={{ fontSize: 12, color: "#94a3b8" }}>A</span>

                                <Slider
                                    size="small"
                                    min={12}
                                    max={28}
                                    step={1}
                                    value={fontSize}
                                    onChange={(_e, value) => setFontSize(value)}
                                    sx={{
                                        width: 100,
                                        color: "#7c3aed",
                                        "& .MuiSlider-thumb": {
                                            width: 12,
                                            height: 12,
                                            "&:hover, &.Mui-focusVisible": {
                                                boxShadow: "0 0 0 6px rgba(124,58,237,0.16)",
                                            },
                                        },
                                    }}
                                />

                                <span style={{ fontSize: 18, color: "#94a3b8" }}>A</span>
                            </div>
                        </div>

                        <div style={{ padding: 20 }}>
                            <textarea
                                ref={areaRef}
                                dir="rtl"
                                value={draft}
                                onChange={(e) => {
                                    setDraft(e.target.value);
                                    onLiveChange(block.id, e.target.value);
                                }}
                                rows={8}
                                style={{
                                    width: "100%",
                                    height: "30vh",
                                    maxHeight: "30vh",
                                    resize: "none",
                                    borderRadius: 12,
                                    border: "1px solid #e4e4e7",
                                    overflowY: "auto",
                                    background: "#fafafa",
                                    padding: 16,
                                    fontSize: `${fontSize}px`,
                                    lineHeight: 1.9,
                                    color: "#18181b",
                                    outline: "none",
                                    fontFamily: "inherit",
                                    boxSizing: "border-box",
                                    transition: "font-size .1s ease",
                                }}
                                onFocus={(e) => (e.target.style.borderColor = "#7c3aed")}
                                onBlur={(e) => (e.target.style.borderColor = "#e4e4e7")}
                            />
                        </div>

                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                                borderTop: "1px solid #f1f5f9",
                                background: "#fafafa",
                                padding: "12px 20px",
                            }}
                        >
                            <span
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    fontSize: 12,
                                    color: "#94a3b8",
                                }}
                            >
                                <RefreshCw size={14} color="#7c3aed" />
                                Edits sync live with the HTML block.
                            </span>

                            <div style={{ display: "flex", gap: 8 }}>
                                <button
                                    onClick={cancel}
                                    style={{
                                        borderRadius: 8,
                                        padding: "8px 14px",
                                        fontSize: 13,
                                        fontWeight: 500,
                                        color: "#27272a",
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                    }}
                                >
                                    Cancel
                                </button>

                                <button
                                    onClick={() => {
                                        onSave(block.id, draft);
                                        onClose();
                                    }}
                                    style={{
                                        borderRadius: 8,
                                        padding: "8px 16px",
                                        fontSize: 13,
                                        fontWeight: 500,
                                        color: "#fff",
                                        background: "#7c3aed",
                                        border: "none",
                                        cursor: "pointer",
                                    }}
                                >
                                    Save changes
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}

BlockEditorModal.propTypes = {
    block: PropTypes.object,
    anchorRect: PropTypes.object, 
    boundsRect: PropTypes.object,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    onLiveChange: PropTypes.func
}